/**
 * Game State Store - Master state for all gameplay
 * Matches architecture specification in docs/architecture/10-frontend-architecture.md
 */

import { writable, derived } from 'svelte/store';
import type { GameState, WordBankEntry, TargetWord, WordBox } from '../types';

/**
 * Helper function to create target word from word bank entry
 */
function createTargetWord(entry: WordBankEntry, difficulty: 'easy' | 'medium' | 'advanced'): TargetWord {
  const boxes: WordBox[] = entry.word.split('').map((letter, index) => ({
    index,
    letter: letter.toLowerCase(),
    filled: false,
    displayLetter: null
  }));

  return {
    word: entry.word,
    imageUrl: entry.imageUrl,
    emoji: entry.emoji,
    boxes,
    difficulty,
    visible: difficulty === 'easy' // Only show word in easy mode
  };
}

/**
 * Create the game state store with actions
 */
function createGameStore() {
  const initialState: GameState = {
    mode: { mode: 'menu', difficulty: null },
    player: {
      position: { x: 0, y: 0 },
      velocity: { x: 0, y: 0 },
      size: 80,
      sprite: '',
      type: 'letter'
    },
    score: 0,
    objects: [],
    letters: [],
    targetWord: null,
    streak: 0,
    sequenceIndex: 0,
    isPaused: false
  };

  const { subscribe, set, update } = writable<GameState>(initialState);

  return {
    subscribe,
    set,
    update,

    /**
     * Start Letter Mode with a specific letter
     */
    startLetterMode: (letter: string) => {
      update(state => ({
        ...state,
        mode: { mode: 'letter', difficulty: null },
        player: {
          ...state.player,
          currentLetter: letter,
          type: 'letter',
          position: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
          velocity: { x: 0, y: 0 }
        },
        score: 0,
        streak: 0,
        objects: [],
        letters: [],
        targetWord: null,
        sequenceIndex: 0,
        isPaused: false
      }));
    },

    /**
     * Start Monster Mode with difficulty and target word
     */
    startMonsterMode: (difficulty: 'easy' | 'medium' | 'advanced', word: WordBankEntry) => {
      update(state => ({
        ...state,
        mode: { mode: 'monster', difficulty },
        player: {
          ...state.player,
          type: 'monster',
          currentLetter: undefined,
          position: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
          velocity: { x: 0, y: 0 }
        },
        score: 0,
        streak: 0,
        sequenceIndex: 0,
        targetWord: createTargetWord(word, difficulty),
        objects: [],
        letters: [],
        isPaused: false
      }));
    },

    /**
     * Return to main menu
     */
    returnToMenu: () => {
      set(initialState);
    },

    /**
     * Add points to the score
     */
    addScore: (points: number) => {
      update(state => ({ ...state, score: state.score + points }));
    },

    /**
     * Increment the streak counter
     */
    incrementStreak: () => {
      update(state => ({ ...state, streak: state.streak + 1 }));
    },

    /**
     * Reset the streak counter
     */
    resetStreak: () => {
      update(state => ({ ...state, streak: 0 }));
    },

    /**
     * Toggle pause state
     */
    togglePause: () => {
      update(state => ({ ...state, isPaused: !state.isPaused }));
    },

    /**
     * Update player position
     */
    updatePlayerPosition: (position: { x: number; y: number }) => {
      update(state => ({
        ...state,
        player: { ...state.player, position }
      }));
    },

    /**
     * Update player velocity
     */
    updatePlayerVelocity: (velocity: { x: number; y: number }) => {
      update(state => ({
        ...state,
        player: { ...state.player, velocity }
      }));
    },

    /**
     * Add a game object to the scene (Letter Mode)
     */
    addObject: (object: GameState['objects'][0]) => {
      update(state => ({
        ...state,
        objects: [...state.objects, object]
      }));
    },

    /**
     * Remove an object by ID
     */
    removeObject: (id: string) => {
      update(state => ({
        ...state,
        objects: state.objects.filter(obj => obj.id !== id)
      }));
    },

    /**
     * Add a letter to the scene (Monster Mode)
     */
    addLetter: (letter: GameState['letters'][0]) => {
      update(state => ({
        ...state,
        letters: [...state.letters, letter]
      }));
    },

    /**
     * Mark a letter as collected
     */
    collectLetter: (id: string) => {
      update(state => ({
        ...state,
        letters: state.letters.map(letter =>
          letter.id === id ? { ...letter, collected: true } : letter
        )
      }));
    },

    /**
     * Fill a word box with a letter (Monster Mode)
     */
    fillWordBox: (index: number, letter: string) => {
      update(state => {
        if (!state.targetWord) return state;

        const newBoxes = state.targetWord.boxes.map(box =>
          box.index === index
            ? { ...box, filled: true, displayLetter: letter }
            : box
        );

        return {
          ...state,
          targetWord: { ...state.targetWord, boxes: newBoxes },
          sequenceIndex: index + 1
        };
      });
    },

    /**
     * Update all game objects positions
     */
    updateObjects: (objects: GameState['objects']) => {
      update(state => ({ ...state, objects }));
    },

    /**
     * Update all letters positions
     */
    updateLetters: (letters: GameState['letters']) => {
      update(state => ({ ...state, letters }));
    }
  };
}

export const gameState = createGameStore();

// Derived stores for computed values
export const isGameActive = derived(
  gameState,
  $gameState => $gameState.mode.mode !== 'menu'
);

export const currentScore = derived(
  gameState,
  $gameState => $gameState.score
);

export const isLetterMode = derived(
  gameState,
  $gameState => $gameState.mode.mode === 'letter'
);

export const isMonsterMode = derived(
  gameState,
  $gameState => $gameState.mode.mode === 'monster'
);

export const wordProgress = derived(
  gameState,
  $gameState => {
    if (!$gameState.targetWord) return 0;
    const filled = $gameState.targetWord.boxes.filter(box => box.filled).length;
    const total = $gameState.targetWord.boxes.length;
    return total > 0 ? (filled / total) * 100 : 0;
  }
);

export const isWordComplete = derived(
  gameState,
  $gameState => {
    if (!$gameState.targetWord) return false;
    return $gameState.targetWord.boxes.every(box => box.filled);
  }
);
